import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import useUserStore from "@/store/useUserStore";
import type { SpecialLocation } from "@/types/specialLocations";
import type { TileCoordinates } from "@/types/tile";
import { TileEntityParam, TileLabelSkin, TileLabelType } from "@/types/tile";
import {
	getSpecialLocationConfigById,
	getSpecialLocationsOrderedByDistance,
} from "./entities/config/specialLocations";
import { TileVisibilityType } from "./entities/config/terrain";
import TileLabel from "./TileLabel";
import { TileToastType } from "./ToastMessage";

export const getOldSpecialLocations = (
	scene: Phaser.Scene,
): Array<SpecialLocationRepository | null> => {
	const userStore = useUserStore();
	const locations: Array<SpecialLocationRepository | null> = [];
	getSpecialLocationsOrderedByDistance();
	const tilerepo = useTileRepository();
	const specialLocations: Array<SpecialLocation> = [];
	userStore.activeSpecialLocationsIds.forEach((id: number): void => {
		const config = getSpecialLocationConfigById(id);
		if (!config) return;
		specialLocations.push(config);
	});
	for (const key in specialLocations) {
		const specialLocation = specialLocations[key];
		const tile = tilerepo.getTileEntityByTileCoordinates(
			specialLocation.coordinates,
		);
		if (
			tile?.getParamByName(TileEntityParam.VISIBILITY) !==
			TileVisibilityType.EXPLORED
		) {
			locations.push(
				new SpecialLocationRepository(
					specialLocation.key,
					specialLocation.coordinates,
					specialLocation.distance,
					new TileLabel(
						scene,
						specialLocation.coordinates,
						true,
						"",
						TileLabelType.MISC,
						TileLabelSkin.SPECIAL_LOCATION,
					),
				),
			);
		} else {
			userStore.setActiveSpecialLocationsIds(
				userStore.activeSpecialLocationsIds.filter(
					(id: number): boolean => id !== specialLocation.key,
				),
			);
		}
	}
	return locations;
};

export const getNewSpecialLocation = (
	scene: Phaser.Scene,
): SpecialLocationRepository | null => {
	const userStore = useUserStore();
	if (userStore.activeSpecialLocationsIds.length >= 3) return null;

	const specialLocations = getSpecialLocationsOrderedByDistance();
	const tilerepo = useTileRepository();
	for (const key in specialLocations) {
		const specialLocation = specialLocations[key];
		const tile = tilerepo.getTileEntityByTileCoordinates(
			specialLocation.coordinates,
		);
		if (
			tile?.getParamByName(TileEntityParam.VISIBILITY) !==
				TileVisibilityType.EXPLORED &&
			!userStore.activeSpecialLocationsIds.includes(specialLocation.key)
		) {
			const emitter = useEmitter();
			emitter.emit(EmitterEvents.PhaserTextMessage, {
				text: "ui.special_location_found",
				type: TileToastType.SUCCESS,
			});
			return new SpecialLocationRepository(
				specialLocation.key,
				specialLocation.coordinates,
				specialLocation.distance,
				new TileLabel(
					scene,
					specialLocation.coordinates,
					true,
					"",
					TileLabelType.MISC,
					TileLabelSkin.SPECIAL_LOCATION,
				),
			);
		}
	}
	return null;
};

export default class SpecialLocationRepository {
	public constructor(
		public readonly id: number,
		public readonly coordinates: TileCoordinates,
		public readonly distance: number,
		public readonly tileLabel: TileLabel,
	) {
		const userStore = useUserStore();
		if (userStore.activeSpecialLocationsIds.includes(this.id)) return;
		userStore.setActiveSpecialLocationsIds([
			...userStore.activeSpecialLocationsIds,
			this.id,
		]);
	}

	public delete(): void {
		this.tileLabel.delete();
		const userStore = useUserStore();
		userStore.setActiveSpecialLocationsIds(
			userStore.activeSpecialLocationsIds.filter(id => id !== this.id),
		);
	}
}
